import { z } from 'zod';
import { DELIVERY_RANGE_DAYS, type DeliveryRangeDays } from '../../../lib/deliveryRange';

// `origin` records WHICH DOOR the agent came through, and it is carried in the URL rather than
// derived because it cannot be derived: under the titular's branch a tracking search produces a
// `kind: 'user'` tab, indistinguishable from a phone search, and the audience the «Acciones» card
// opens in differs between the two. Optional, so every tab written before it still parses.
const deliverySubtabSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  origin: z.literal('tracking').optional(),
});
const searchTabSchema = z.object({
  key: z.string(),
  label: z.string().optional(),
  deliveries: z.array(deliverySubtabSchema),
  activeSubtab: z.string(),
  kind: z.enum(['user', 'location', 'delivery']).default('user'),
  filters: z
    .object({
      // A carrier's INTEGER id, which is what the read filters by — the column behind it is an
      // indexed FK and two live carriers share the name «Amazon», so a name could never key a
      // query. Absent is «Todos los carriers»; `.positive()` keeps that the only way to say it, so
      // a `carrier` that IS there always names a real one.
      carrier: z.number().int().positive().optional(),
      // The name to SHOW, stored beside the id and never sent anywhere, so a tab restored from the
      // URL can render its carrier chip without waiting on the catalog read — no loading state was
      // designed for a chip. `.min(1)` because a chip with nothing to say is not a filter; it may
      // go stale if backend renames a carrier, and self-heals when the agent re-picks one.
      carrierLabel: z.string().min(1).optional(),
      // The five ranges have ONE definition (`lib/deliveryRange`); this reuses it rather than
      // restating them as a literal union, so a range added there can never be one the URL
      // rejects while the dropdown offers it. `custom` and not `z.union` because the source is a
      // readonly array, not a literal tuple — the predicate is what carries the narrowing.
      days: z.custom<DeliveryRangeDays>((v): v is DeliveryRangeDays =>
        DELIVERY_RANGE_DAYS.some((d) => d === v),
      ),
      // `.min(1)`, not a bare optional string: an emptied «Número de buzón» is not a filter, so
      // absent-or-non-empty is settled here rather than by each reader downstream.
      box: z.string().min(1).optional(),
      // The marked states, as the wire spells them — and NO label beside them, unlike the carrier:
      // the copy for a state resolves synchronously from the app's own vocabulary, so storing it
      // would only put a second, staleable spelling in the URL.
      //
      // Absent-or-non-empty again, and settled here for the same reason: this is the producer, so
      // the chip, the request and the analytics dimension can all read `states !== undefined` as
      // «a state filter is applied» without re-asking. `.min(1)` on the array and NOT
      // `.nonempty()`, which infers a tuple in Zod 3 that the plain `string[]` of the option lib
      // cannot be assigned to.
      states: z.array(z.string().min(1)).min(1).optional(),
    })
    .optional(),
});
// The ENVELOPE. `searches` is a list of unknowns on purpose, so each tab can be parsed — and
// rejected — on its own: see `decodeShellState`. There is deliberately NO composed
// `z.object({ searches: z.array(searchTabSchema) })`: it would be a second, unused definition of a
// shape this file already assembles in two halves, and the day the two disagreed the dead one would
// be the readable-looking one.
const shellEnvelopeSchema = z.object({
  searches: z.array(z.unknown()),
  activeKey: z.string().nullable(),
});

export type DeliverySubtab = z.infer<typeof deliverySubtabSchema>;
export type SearchTab = z.infer<typeof searchTabSchema>;

// Composed from the two halves above rather than inferred from a schema of its own, mirroring how
// `decodeShellState` builds it: a validated envelope plus the tabs that survived parsing.
export interface ShellState {
  searches: SearchTab[];
  activeKey: string | null;
}

// The root sub-tab of any search, whatever the search is for. The VALUE stays 'user' on purpose:
// it is a wire value, and every URL already stored out there carries it.
export const ROOT_SUBTAB = 'user';
export const EMPTY_SHELL_STATE: ShellState = { searches: [], activeKey: null };

export type LocationFilters = NonNullable<SearchTab['filters']>;

export interface LocationTab extends SearchTab {
  kind: 'location';
  label: string;
  filters: LocationFilters;
}

// The shape a location tab has once it is in `searches` — normalization drops any that is not
// one. Exported as a predicate rather than left implicit so a consumer can READ `label` and
// `filters` without a fallback the invariant has already made unreachable; `normalizeShellState`
// filters on this same function, so the guarantee and the type cannot disagree.
export function isLocationTab(tab: SearchTab): tab is LocationTab {
  return tab.kind === 'location' && tab.label !== undefined && tab.filters !== undefined;
}

// `searches` is one flat list and every consumer matches on `key` alone — the active-tab
// lookup, the tab list, close, the per-tab content key. So keys must be unique across kinds:
// a phone stored as bare digits that equalled a location id would put two tabs under one key,
// and only the first would ever render. The prefix makes that collision impossible instead of
// leaving each consumer to defend against it.
const LOCATION_KEY_PREFIX = 'loc:';
const DELIVERY_KEY_PREFIX = 'dlv:';

// The one boundary between the two representations, and it is explicit: a tab KEY is a string
// (`loc:<id>`, because keys of both kinds share one flat namespace), while a location ID is the
// integer the read takes.
export function locationTabKey(id: number): string {
  return `${LOCATION_KEY_PREFIX}${String(id)}`;
}

// Total on purpose: an un-namespaced key degrades to a read that fails and renders the tab's error
// strip, never to an undefined travelling into a request path. A key carrying no number yields
// `NaN`, which degrades the same way — a request for a location that cannot exist — rather than
// becoming an empty path segment.
export function locationIdFromTabKey(key: string): number {
  return Number(key.startsWith(LOCATION_KEY_PREFIX) ? key.slice(LOCATION_KEY_PREFIX.length) : key);
}

// The delivery's id is a STRING (a uuid), unlike a location's integer, so this pair is a prefix and
// nothing more — no Number() in either direction.
export function deliveryTabKey(id: string): string {
  return `${DELIVERY_KEY_PREFIX}${id}`;
}

// Total, like its location sibling: an un-namespaced key degrades to a read that fails and renders
// the tab's error strip, never to an `undefined` travelling into a request path.
export function deliveryIdFromTabKey(key: string): string {
  return key.startsWith(DELIVERY_KEY_PREFIX) ? key.slice(DELIVERY_KEY_PREFIX.length) : key;
}

export interface DeliveryTab extends SearchTab {
  kind: 'delivery';
}

// The shape a delivery tab has once it is in `searches`. What makes this kind unusable is not a
// missing field but an unreadable KEY: without an id it can never read the delivery it exists to
// show, and there is no way in to supply one. Same doctrine as `isLocationTab`, and
// `normalizeShellState` filters on this very function so the guarantee and the type cannot
// disagree.
export function isDeliveryTab(tab: SearchTab): tab is DeliveryTab {
  return tab.kind === 'delivery' && deliveryIdFromTabKey(tab.key) !== '';
}

export function encodeShellState(state: ShellState): string {
  return encodeURIComponent(JSON.stringify(state));
}

// Zod checks structure but not cross-field consistency. A hand-edited or stale
// URL can carry an activeKey pointing at no search, or an activeSubtab pointing
// at no delivery — repair both so everything downstream can trust that
// activeKey/activeSubtab always reference something that exists.
export function normalizeShellState(state: ShellState): ShellState {
  const searches = state.searches
    .map((s) =>
      s.activeSubtab === ROOT_SUBTAB || s.deliveries.some((d) => d.id === s.activeSubtab)
        ? s
        : { ...s, activeSubtab: ROOT_SUBTAB },
    )
    // A location tab without filters can render nothing and offers no way in to supply them;
    // without a label it cannot say WHICH building. Both are permanently unusable, so drop the
    // one rather than collapse the whole state and lose its healthy siblings.
    .filter((s) => s.kind !== 'location' || isLocationTab(s))
    // And the same for the third kind: a delivery tab with no id in its key is permanently blind.
    .filter((s) => s.kind !== 'delivery' || isDeliveryTab(s));
  const activeKey = searches.some((s) => s.key === state.activeKey)
    ? state.activeKey
    : (searches[0]?.key ?? null);
  return { searches, activeKey };
}

// Tolerant decode, at TWO different granularities, and the difference is the point.
//
// The ENVELOPE fails closed: a `searches` that is not a list, or JSON that is not an object, is not
// a shell state at all and collapses to the empty one — a hand-edited URL never crashes the shell.
//
// A TAB degrades ALONE. Parsing the whole state in one `safeParse` used to mean that one tab the
// schema could not read — an unrecognized `kind` from a newer deploy, a hand-edited fragment, a
// field that moved — collapsed the entire state and closed every other tab the agent had open. The
// blast radius of one unreadable tab is now that tab. It is the same doctrine the location filter
// already followed («drop the one rather than lose its healthy siblings»), applied one level up.
export function decodeShellState(raw: string | null): ShellState {
  if (!raw) return EMPTY_SHELL_STATE;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    const envelope = shellEnvelopeSchema.safeParse(parsed);
    if (!envelope.success) return EMPTY_SHELL_STATE;
    const searches = envelope.data.searches
      .map((tab) => searchTabSchema.safeParse(tab))
      .filter((result) => result.success)
      .map((result) => result.data);
    return normalizeShellState({ searches, activeKey: envelope.data.activeKey });
  } catch {
    return EMPTY_SHELL_STATE;
  }
}
